import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import {
  DndContext, PointerSensor, useSensor, useSensors, closestCenter,
} from "@dnd-kit/core";
import {
  arrayMove, SortableContext, useSortable, verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, GripVertical, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

const PRESET_COLORS = ["#3b82f6", "#f59e0b", "#10b981", "#8b5cf6", "#06b6d4", "#22c55e", "#ef4444", "#ec4899", "#71717a", "#f97316"];

function SortableRow({ status, onEdit, onDelete, canManage }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: status.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-3 p-3 bg-card border border-border rounded-md hover:border-foreground/20 transition-colors"
      data-testid={`status-row-${status.name}`}
    >
      {canManage && (
        <button {...attributes} {...listeners} className="text-muted-foreground hover:text-foreground cursor-grab active:cursor-grabbing">
          <GripVertical className="h-4 w-4" />
        </button>
      )}
      <span className="h-3 w-3 rounded-full shrink-0" style={{ background: status.color }} />
      <span className="text-sm font-medium flex-1">{status.name}</span>
      {status.is_default && <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Implicit</span>}
      {status.is_terminal && <span className="text-[10px] uppercase tracking-widest text-amber-500 font-semibold">Terminal</span>}
      {canManage && (
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onEdit(status)} data-testid={`edit-status-${status.name}`}>
            <Pencil className="h-3.5 w-3.5" />
          </Button>
          {!status.is_default && (
            <Button variant="ghost" size="icon" className="h-7 w-7 hover:text-red-500" onClick={() => onDelete(status)} data-testid={`delete-status-${status.name}`}>
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

export default function StatusesPage() {
  const { hasPermission } = useAuth();
  const canManage = hasPermission("manage_statuses");
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", color: PRESET_COLORS[0], is_terminal: false });

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const load = async () => {
    try {
      const { data } = await api.get("/returns/statuses");
      setStatuses(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ name: "", color: PRESET_COLORS[0], is_terminal: false });
    setOpenDialog(true);
  };
  const openEdit = (s) => {
    setEditing(s);
    setForm({ name: s.name, color: s.color, is_terminal: !!s.is_terminal });
    setOpenDialog(true);
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Nume necesar");
    try {
      if (editing) {
        await api.patch(`/returns/statuses/${editing.id}`, form);
        toast.success("Status actualizat");
      } else {
        await api.post("/returns/statuses", { ...form, order: statuses.length });
        toast.success("Status creat");
      }
      setOpenDialog(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (s) => {
    if (!confirm(`Ștergi statusul "${s.name}"?`)) return;
    try {
      await api.delete(`/returns/statuses/${s.id}`);
      toast.success("Status șters");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const onDragEnd = async (e) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIdx = statuses.findIndex((s) => s.id === active.id);
    const newIdx = statuses.findIndex((s) => s.id === over.id);
    const reordered = arrayMove(statuses, oldIdx, newIdx);
    setStatuses(reordered);
    try {
      await api.post("/returns/statuses/reorder", { order: reordered.map((s) => s.id) });
    } catch (err) { toast.error("Reordonarea a eșuat"); load(); }
  };

  return (
    <div className="space-y-6" data-testid="statuses-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2">Setări · Retururi</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Statusuri custom</h1>
          <p className="text-muted-foreground text-sm mt-1">Configurează statusurile prin care trec retururile.</p>
        </div>
        {canManage && (
          <Button onClick={openCreate} data-testid="add-status-btn">
            <Plus className="h-4 w-4 mr-1.5" /> Status nou
          </Button>
        )}
      </div>

      <Card className="p-4 bg-card border-border">
        {loading ? (
          <div className="text-sm text-muted-foreground p-4">Se încarcă…</div>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <SortableContext items={statuses.map((s) => s.id)} strategy={verticalListSortingStrategy}>
              <div className="space-y-2">
                {statuses.map((s) => (
                  <SortableRow key={s.id} status={s} onEdit={openEdit} onDelete={del} canManage={canManage} />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </Card>

      <Dialog open={openDialog} onOpenChange={setOpenDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? "Editează status" : "Status nou"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Nume</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="ex: În verificare" data-testid="status-name-input" />
            </div>
            <div className="space-y-2">
              <Label>Culoare</Label>
              <div className="flex flex-wrap gap-2">
                {PRESET_COLORS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setForm({ ...form, color: c })}
                    className={`h-8 w-8 rounded-md border-2 transition-all ${form.color === c ? "border-foreground scale-110" : "border-transparent"}`}
                    style={{ background: c }}
                    data-testid={`color-${c}`}
                  />
                ))}
              </div>
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="terminal" className="cursor-pointer">Status final (închide retururile)</Label>
              <Switch id="terminal" checked={form.is_terminal} onCheckedChange={(v) => setForm({ ...form, is_terminal: v })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenDialog(false)}>Anulează</Button>
            <Button onClick={save} data-testid="save-status-btn">{editing ? "Salvează" : "Creează"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
